import { FiCheck, FiClock, FiX, FiMail, FiActivity } from "react-icons/fi";
import { cn } from "utils/helpers";
import type { PanelMemberStatus } from "../../types/panel.type";

/**
 * Single source of truth for how a panel member's status is presented.
 * Used by both the Evaluation Panel setup screen and the Initial/Final
 * Evaluation > Panel Members views so the pills stay consistent.
 */

export const getStatusIcon = (status: PanelMemberStatus): React.ReactNode => {
	switch (status) {
		case "submitted":
			return <FiCheck size={12} aria-hidden="true" />;
		case "accepted":
		case "opened":
		case "in_progress":
			return <FiActivity size={12} aria-hidden="true" />;
		case "invited":
			return <FiMail size={12} aria-hidden="true" />;
		case "declined":
		case "revoked":
			return <FiX size={12} aria-hidden="true" />;
		default:
			return <FiClock size={12} aria-hidden="true" />;
	}
};

export const getStatusBadgeClasses = (status: PanelMemberStatus): string => {
	switch (status) {
		case "submitted":
			return "bg-green-100 text-green-700";
		case "accepted":
		case "opened":
		case "in_progress":
			return "bg-[#e8eeff] text-[#0031DD]";
		case "invited":
			return "bg-amber-100 text-amber-700";
		case "declined":
			return "bg-red-100 text-red-700";
		case "revoked":
			return "bg-gray-200 text-gray-500";
		default:
			return "bg-gray-100 text-gray-600";
	}
};

export const getStatusLabel = (status: PanelMemberStatus): string => {
	switch (status) {
		case "submitted":
			return "Submitted";
		case "accepted":
			return "Accepted";
		case "opened":
			return "Opened";
		case "in_progress":
			return "In Progress";
		case "invited":
			return "Invited";
		case "declined":
			return "Declined";
		case "revoked":
			return "Revoked";
		case "pending":
			return "Pending";
		default:
			return status;
	}
};

interface MemberStatusBadgeProps {
	status: PanelMemberStatus;
	className?: string;
}

export const MemberStatusBadge: React.FC<MemberStatusBadgeProps> = ({ status, className }) => {
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
				getStatusBadgeClasses(status),
				className
			)}
			data-testid={`member-status-badge-${status}`}
		>
			{getStatusIcon(status)}
			{getStatusLabel(status)}
		</span>
	);
};
